import { computed, type MaybeRef, unref } from "vue";
import type { DataSet, NumericDomain } from "@incytestudios/ddv";
import type { OperatingPoint } from "@/components/types";

interface OperatingPointsBufferConfig {
  /** Operating points to check for min/max values */
  operatingPoints: MaybeRef<OperatingPoint[] | undefined>;
  /** Dataset points to compare against */
  tableDataSets: MaybeRef<DataSet[]>;
  bufferPercentage?: number;
}

/**
 * A composable that calculates domain boundaries with buffers when operating points
 * represent min/max values outside the bounds of the provided dataset domains.
 */
export function useOperatingPointsInAggregatedDataSets(
  config: OperatingPointsBufferConfig
) {
  const bufferPercentage = config.bufferPercentage || 0.05; // Default to 5%

  // Calculate domain boundaries from datasets
  const calculatedDomains = computed(() => {
    let minX = Infinity;
    let maxX = -Infinity;
    let minY = Infinity;
    let maxY = -Infinity;

    // Get min/max values from all datasets
    const datasets = unref(config.tableDataSets);
    datasets.forEach(dataset => {
      dataset.points.forEach(point => {
        minX = Math.min(minX, point.x as number);
        maxX = Math.max(maxX, point.x as number);
        minY = Math.min(minY, point.y as number);
        maxY = Math.max(maxY, point.y as number);
      });
    });

    return { minX, maxX, minY, maxY };
  });

  // Identify operating points that are min/max values relative to tableDatasets
  const operatingPointsMinMax = computed(() => {
    const result = {
      minX: { isMin: false, value: Infinity },
      maxX: { isMax: false, value: -Infinity },
      minY: { isMin: false, value: Infinity },
      maxY: { isMax: false, value: -Infinity }
    };

    const {
      minX: tableDatasetsMinX,
      maxX: tableDatasetsMaxX,
      minY: tableDatasetsMinY,
      maxY: tableDatasetsMaxY
    } = calculatedDomains.value;

    // Check if any operating point is a min/max
    const opPoints = unref(config.operatingPoints);
    if (opPoints && opPoints.length > 0) {
      opPoints.forEach(op => {
        const point = op.point;

        // Check X min/max
        if (point.x <= tableDatasetsMinX && point.x < result.minX.value) {
          result.minX = { isMin: true, value: point.x as number };
        }
        if (point.x >= tableDatasetsMaxX && point.x > result.maxX.value) {
          result.maxX = { isMax: true, value: point.x as number };
        }

        // Check Y min/max
        if (point.y <= tableDatasetsMinY && point.y < result.minY.value) {
          result.minY = { isMin: true, value: point.y as number };
        }
        if (point.y >= tableDatasetsMaxY && point.y > result.maxY.value) {
          result.maxY = { isMax: true, value: point.y as number };
        }
      });
    }

    return result;
  });

  const operatingPointIsMinOrMax = computed(() => {
    return (
      operatingPointsMinMax.value.minY.isMin ||
      operatingPointsMinMax.value.maxY.isMax ||
      operatingPointsMinMax.value.minX.isMin ||
      operatingPointsMinMax.value.maxX.isMax
    );
  });

  // Calculate padded domain X with buffer for min/max operating points
  const paddedDomainX = computed<NumericDomain>(() => {
    const { minX, maxX } = calculatedDomains.value;
    let min = minX;
    let max = maxX;

    // Apply buffer if operating points are min/max
    if (operatingPointsMinMax.value.minX.isMin) {
      const range = max - min;
      const buffer = range * bufferPercentage;
      // Ensure we don't go below 0 with the buffer
      min = Math.max(0, min - buffer);
    }

    if (operatingPointsMinMax.value.maxX.isMax) {
      const range = max - min;
      const buffer = range * bufferPercentage;
      max = max + buffer;
    }

    return { min, max };
  });

  // Calculate padded domain Y with buffer for min/max operating points
  const paddedDomainY = computed<NumericDomain>(() => {
    const { minY, maxY } = calculatedDomains.value;
    let min = minY;
    let max = maxY;

    // Apply buffer if operating points are min/max
    if (operatingPointsMinMax.value.minY.isMin) {
      const range = max - min;
      const buffer = range * bufferPercentage;
      // Ensure we don't go below 0 with the buffer
      min = Math.max(0, min - buffer);
    }

    if (operatingPointsMinMax.value.maxY.isMax) {
      const range = max - min;
      const buffer = range * bufferPercentage;
      max = max + buffer;
    }

    return { min, max };
  });

  const aggregatedDataSets = computed<DataSet[]>(() => {
    if (operatingPointIsMinOrMax.value) {
      return [
        {
          id: "operating-points-buffer",
          points: [
            { x: paddedDomainX.value.min, y: paddedDomainY.value.min },
            { x: paddedDomainX.value.max, y: paddedDomainY.value.min }
          ],
          domainX: paddedDomainX.value,
          domainY: paddedDomainY.value
        },
        ...unref(config.tableDataSets)
      ];
    }
    return unref(config.tableDataSets);
  });

  return aggregatedDataSets;
}
